// BOORO: workspace areas above the chats in the sidebar, each rendered in place of the chat.
import { useLingui } from "@lingui/react/macro";
import type { LucideIcon } from "lucide-react";
import { BookOpen, House, Images, ListTodo, Network, ScrollText } from "lucide-react";
import { AssetsView } from "./assets-view";
import { ChiefWidget } from "./chief-widget";
import { InboxView } from "./inbox-view";
import { KnowledgeView } from "./knowledge-view";
import { LogView } from "./log-view";
import { TasksView } from "./tasks-view";
import { TeamView } from "./team-view";
import type { WorkspaceChrome } from "./workspace-header";
import { WorkspaceViewHeader } from "./workspace-header";

export const WORKSPACE_VIEWS = ["start", "tasks", "team", "knowledge", "assets", "log"] as const;
export type WorkspaceView = (typeof WORKSPACE_VIEWS)[number];

export function parseWorkspaceView(value: string | undefined): WorkspaceView | null {
  return WORKSPACE_VIEWS.find((view) => view === value) ?? null;
}

export function workspaceViewPath(view: WorkspaceView): string {
  return `/app/v/${view}`;
}

/** Tasks live on the ticket board, so the area exists only while the board is enabled. */
export function visibleWorkspaceViews(ticketBoardEnabled: boolean): readonly WorkspaceView[] {
  const views = ticketBoardEnabled
    ? WORKSPACE_VIEWS
    : WORKSPACE_VIEWS.filter((view) => view !== "tasks");
  // BOORO: the log is an admin task and stays out of the sidebar.
  return views.filter((view) => view !== "log");
}

const VIEW_ICONS: Record<WorkspaceView, LucideIcon> = {
  start: House,
  tasks: ListTodo,
  team: Network,
  knowledge: BookOpen,
  assets: Images,
  log: ScrollText,
};

function useWorkspaceViewLabels(): Record<WorkspaceView, string> {
  const { t } = useLingui();
  return {
    start: t`Start`,
    tasks: t`Tasks`,
    team: t`Team`,
    knowledge: t`Knowledge`,
    assets: t`Assets`,
    log: t`Log`,
  };
}

export function WorkspaceNav({
  views,
  active,
  onOpen,
  counts,
}: {
  views: readonly WorkspaceView[];
  active: WorkspaceView | null;
  onOpen: (view: WorkspaceView) => void;
  /** Open entries per view, shown as a badge. */
  counts?: Partial<Record<WorkspaceView, number>>;
}) {
  const labels = useWorkspaceViewLabels();
  return (
    <nav data-testid="workspace-nav" className="mb-3 flex flex-col gap-0.5 px-2.5">
      {views.map((view) => {
        const Icon = VIEW_ICONS[view];
        const current = view === active;
        const count = counts?.[view] ?? 0;
        return (
          <button
            key={view}
            type="button"
            data-workspace-view={view}
            aria-current={current ? "page" : undefined}
            onClick={() => onOpen(view)}
            className={`flex items-center gap-3 rounded-lg px-2.5 py-2 text-[14px] ${
              current
                ? "bg-sidebar-accent font-medium text-foreground"
                : "text-foreground/80 hover:bg-sidebar-accent"
            }`}
          >
            <Icon
              size={16}
              strokeWidth={1.8}
              className="text-muted-foreground"
              aria-hidden="true"
            />
            {labels[view]}
            {count > 0 ? (
              <span
                data-testid={`workspace-nav-count-${view}`}
                className="ms-auto rounded-full bg-primary px-1.5 py-0.5 text-[11px] font-semibold text-primary-foreground"
              >
                {count}
              </span>
            ) : null}
          </button>
        );
      })}
    </nav>
  );
}

export function WorkspaceViewMain({
  view,
  chrome,
  mobileSidebarOpen,
}: {
  view: WorkspaceView;
  chrome: WorkspaceChrome;
  mobileSidebarOpen: boolean;
}) {
  const labels = useWorkspaceViewLabels();
  return (
    <main
      data-space-switch="main"
      data-workspace-view={view}
      aria-hidden={mobileSidebarOpen || undefined}
      inert={mobileSidebarOpen}
      className="isolate flex min-w-0 flex-1 flex-col bg-background"
    >
      {view === "tasks" ? (
        <TasksView title={labels.tasks} chrome={chrome} />
      ) : view === "start" ? (
        <InboxView title={labels.start} chrome={chrome} />
      ) : view === "log" ? (
        <LogView title={labels.log} chrome={chrome} />
      ) : view === "team" ? (
        <TeamView title={labels.team} chrome={chrome} />
      ) : view === "knowledge" ? (
        <KnowledgeView title={labels.knowledge} chrome={chrome} />
      ) : view === "assets" ? (
        <AssetsView title={labels.assets} chrome={chrome} />
      ) : (
        <>
          <WorkspaceViewHeader title={labels[view]} chrome={chrome} />
          <div className="rk-scroll flex-1 overflow-y-auto" />
        </>
      )}
      {/* BOORO: the Chief is reachable from every area. */}
      <ChiefWidget />
    </main>
  );
}
